import MojitoServices from 'mojito/services';

import {resolveBackground, resolveBorderColor} from '#core/utils/color-utils.js';
import {icon} from '#core/utils/style-utils.js';

import {BasePalette} from '#core/application/modules/base-palette.js';
import {Bonuses} from '#core/application/modules/bonuses/bonuses.js';
import {CLOSE_BUTTON_TYPES, generateCloseButton} from '#core/application/modules/tiles/close-button/index.js';
import {generatePopover} from '#core/application/modules/tiles/popover/index.js';
import {generateErrorNotification} from '#core/application/modules/tiles/notification/index.js';
import {Betslip} from '#core/application/modules/betting-center/betslip/betslip.js';
import {generateBetStatusIcon} from '#core/application/modules/tiles/bet-status-icon/index.js';

import countriesMap from './countries.json';

const LOCATION = MojitoServices.Promotions.types.LOCATION;

export function applicationViewFactory(PALETTE) {
    return {
        AppViewSportsbook: {
            accaBar: {
                hide: Betslip.isFloating,
            },
            betslipPromotions: {
                locationId: LOCATION.RIGHT,
            },
            style: {
                container: {
                    _desktop_maxWidth: '1440px',
                },
                mainContainer: {
                    ...resolveBackground(PALETTE.APPLICATION_PAGE_BG),
                    fontFamily: BasePalette.palette.FONT_FAMILY,
                    userSelect: 'text',
                    ...PALETTE.APPLICATION_PAGE_TEXT,
                },
            },
            backToTopButtonConfig: {
                button: {
                    style: {
                        base: {
                            whiteSpace: 'nowrap',
                            margin: '80px 0 0 220px',
                            _mobile_margin: '80px 0 0 0',
                            padding: 0,
                            _mobile_padding: 0,
                            ...PALETTE.BACK_TO_TOP_BUTTON.TERTIARY_BUTTON_TEXT,
                        },
                    },
                },
                buttonIcon: icon(PALETTE.BACK_TO_TOP_BUTTON.TERTIARY_BUTTON_ICON_COLOR, 'arrow-up'),
            },
        },
        ...commonFactory(PALETTE),
    };
}

// factory for common configuration used in a whole sportsbook
function commonFactory(PALETTE) {
    const INDICATOR = {
        style: {
            base: {
                height: 4,
                bottom: 0,
                left: 8,
                right: 8,
                width: 'initial',
            },
        },
    };

    return {
        UserBalance: {
            includeBonus: Bonuses.showBonusesInMenu,
            includeFreeBets: Bonuses.showVouchersInMenu,
        },
        Button: {
            enableAHref: true,
        },
        TextView: {
            header: {
                style: {
                    padding: '4px 0',
                },
            },
        },
        TextInput: {
            style: {
                base: {
                    fontFamily: 'inherit', // this is needed to reset native browser styles set to input element
                },
            },
        },
        MenuRow: {
            item: {
                // Same as SelectedIndicator below
                indicator: INDICATOR,
            },
        },
        SelectedIndicator: INDICATOR,
        ButtonScrollPane: {
            leftScrollButton: {
                style: {
                    base: {
                        justifyContent: 'center', // align containing icon to the clickable area center
                    },
                },
            },
            rightScrollButton: {
                style: {
                    base: {
                        justifyContent: 'center', // align containing icon to the clickable area center
                    },
                },
            },
        },
        ImageButton: {
            button: {
                style: {
                    base: {
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                },
            },
        },
        CurrentTime: {
            timeFormat: 'HH:mm:ss',
            containerStyle: {
                flexDirection: 'column',
                alignItems: 'left',
            },
            timeStyle: {
                minWidth: '95px',
                fontWeight: 'normal',
            },
        },
        SessionTime: {
            timerHorizontal: {
                timerWrapper: {
                    minWidth: 160,
                },
            },
        },
        CountryIcons: Object.keys(countriesMap).reduce(
            (reducer, country) => ({
                ...reducer,
                [country]: `country-flags/${countriesMap[country]}.svg`,
            }),
            {}
        ),
        MenuLinkPromotions: {
            menuRow: {
                buttonScrollPane: {
                    contentContainer: {
                        style: {
                            _mobile_padding: 0,
                        },
                    },
                },
                item: {
                    unselected: {
                        stopClickEventPropagation: true, //DBX-16975
                        style: {
                            base: {
                                padding: 0,
                            },
                        },
                    },
                },
            },
        },
        Tooltip: {
            popup: generatePopover(PALETTE.COMMON_TOOLTIP_POPOVER),
            textLabel: {
                style: {
                    ...PALETTE.COMMON_TOOLTIP_POPOVER.POPOVER_TEXT,
                },
            },
            image: icon(PALETTE.COMMON_TOOLTIP_POPOVER.POPOVER_ICON_COLOR),
            closeButton: {
                buttonIcon: icon(PALETTE.COMMON_TOOLTIP_POPOVER.POPOVER_ICON_COLOR),
            },
        },
        SlidingPane: {
            overlay: {
                style: {
                    overlay: {
                        ...resolveBackground(PALETTE.SLIDING_PANE_BG),
                    },
                },
            },
            closeButton: generateCloseButton(PALETTE.SLIDING_PANE_CLOSE_BUTTON, CLOSE_BUTTON_TYPES.CIRCULAR),
            closeButtonContainer: {
                style: {
                    left: 'initial', // override mojito's wrong one
                    right: 0,
                    width: 'initial', // override mojito's wrong one
                    height: 'initial', // override mojito's wrong one
                },
            },
        },
        AccaBarView: {
            accaBarInner: {
                style: {
                    ...resolveBackground(PALETTE.ACCA_BAR_BG),
                    ...resolveBorderColor(PALETTE.ACCA_BAR_BORDER_COLOR),
                    borderStyle: 'solid',
                    borderWidth: 1,
                    borderRadius: PALETTE.ACCA_BAR_BORDER_RADIUS,
                    boxShadow: PALETTE.ACCA_BAR_SHADOW,
                    height: 40,
                    minWidth: 216,
                },
            },
            accaBarLabel: {
                style: {
                    ...PALETTE.ACCA_BAR_LABEL_TEXT,
                },
            },
            accaBarImage: icon(PALETTE.ACCA_BAR_CLOSE_BUTTON.CLOSE_BUTTON_ICON_COLOR, 'close'),
            closeButton: {
                style: {
                    base: {
                        borderRadius: 20,
                    },
                },
            },
        },
        ComponentErrorView: {
            container: {
                style: {
                    padding: 8,
                    ...resolveBackground(PALETTE.ERROR_VIEW_BG),
                },
            },
            notification: generateErrorNotification(PALETTE.ERROR_VIEW_ERROR_NOTIFICATION),
            errorDetailsMessage: {
                style: {
                    fontSize: 12,
                    ...PALETTE.ERROR_VIEW_MESSAGE_TEXT,
                },
            },
        },
        LinkMenu: {
            showHeader: true,
        },
        MiniScoreboard: {
            gameActionsIndication: {
                football: {
                    redCard: {
                        visible: true,
                        animated: true,
                    },
                    score: {
                        animated: true,
                    },
                },
            },
        },
        BetStatusIcon: generateBetStatusIcon(PALETTE.BET_STATUS_ICON),
    };
}
